import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, notificationsAPI, contractsAPI } from '../lib/api';
import { Users, UserPlus, Scale, Clock, BarChart3, Settings, Bell, TrendingDown, Calendar, AlertTriangle, FileSignature } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const tiles = [
  { id: 'employees', icon: Users, label: 'Employees', path: '/employees', color: 'from-indigo-500/20' },
  { id: 'add', icon: UserPlus, label: 'Add Employee', path: '/employees/new', color: 'from-emerald-500/20', roles: ['admin', 'hr_manager'] },
  { id: 'disciplinary', icon: Scale, label: 'Disciplinary Cases', path: '/disciplinary', color: 'from-amber-500/20' },
  { id: 'leave', icon: Clock, label: 'Leave & Absence', path: '/leave', color: 'from-purple-500/20' },
  { id: 'reports', icon: BarChart3, label: 'Reports', path: '/reports', color: 'from-rose-500/20', roles: ['admin', 'hr_manager'] },
  { id: 'settings', icon: Settings, label: 'Settings', path: '/settings', color: 'from-slate-500/20', roles: ['admin'] },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [contractStats, setContractStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, isAdmin, isHRManager } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, contractStatsRes] = await Promise.all([
        dashboardAPI.getStats(),
        contractsAPI.getStats(),
      ]);
      setStats(statsRes.data);
      setContractStats(contractStatsRes.data);
      
      // Check notifications if admin or HR
      if (isHRManager()) {
        try {
          await notificationsAPI.checkNotifications();
        } catch (e) {
          console.error('Notification check failed:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTiles = tiles.filter(tile => {
    if (!tile.roles) return true;
    return tile.roles.includes(user?.role);
  });

  const deptData = stats?.department_breakdown?.map((d, i) => ({
    name: d._id || 'Unknown',
    value: d.count,
    color: COLORS[i % COLORS.length]
  })) || [];

  return (
    <div className="space-y-8 page-transition" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
          Welcome back, {user?.full_name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground mt-1">
          Here's what's happening in your organization
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Users}
          label="Total Employees"
          value={stats?.total_employees || 0}
          loading={loading}
          color="text-indigo-400"
          bgColor="bg-indigo-500/20"
          testId="stat-total-employees"
        />
        <StatCard
          icon={Calendar}
          label="On Leave Today"
          value={stats?.on_leave_today || 0}
          loading={loading}
          color="text-emerald-400"
          bgColor="bg-emerald-500/20"
          testId="stat-on-leave"
        />
        <StatCard
          icon={Scale}
          label="Open Cases"
          value={stats?.open_disciplinary_cases || 0}
          loading={loading}
          color="text-amber-400"
          bgColor="bg-amber-500/20"
          testId="stat-open-cases"
        />
        <StatCard
          icon={TrendingDown}
          label="Terminated (90d)"
          value={stats?.terminated_last_90_days || 0}
          loading={loading}
          color="text-red-400"
          bgColor="bg-red-500/20"
          testId="stat-terminated"
        />
      </div>

      {/* Contract Alerts */}
      {contractStats && (contractStats.expired_contracts > 0 || contractStats.expiring_in_7_days > 0) && (
        <div className="glass-card p-6 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <h3 className="text-lg font-semibold text-amber-400">Contract Alerts</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {contractStats.expired_contracts > 0 && (
              <button 
                onClick={() => navigate('/employees?filter=expired')}
                className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-colors text-left"
                data-testid="alert-expired-contracts"
              >
                <p className="text-2xl font-bold text-red-400">{contractStats.expired_contracts}</p>
                <p className="text-sm text-red-400/80">Expired Contracts</p>
              </button>
            )}
            {contractStats.expiring_in_7_days > 0 && (
              <button 
                onClick={() => navigate('/employees?filter=7days')}
                className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors text-left"
                data-testid="alert-expiring-7days"
              >
                <p className="text-2xl font-bold text-amber-400">{contractStats.expiring_in_7_days}</p>
                <p className="text-sm text-amber-400/80">Expiring in 7 days</p>
              </button>
            )}
            {contractStats.expiring_in_14_days > contractStats.expiring_in_7_days && (
              <button 
                onClick={() => navigate('/employees?filter=14days')}
                className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 hover:bg-yellow-500/20 transition-colors text-left"
                data-testid="alert-expiring-14days"
              >
                <p className="text-2xl font-bold text-yellow-400">{contractStats.expiring_in_14_days}</p>
                <p className="text-sm text-yellow-400/80">Expiring in 14 days</p>
              </button>
            )}
            <button 
              onClick={() => navigate('/employees?filterType=contract')}
              className="p-4 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors text-left"
              data-testid="total-contract-employees"
            >
              <p className="text-2xl font-bold text-primary">{contractStats.total_contract_employees}</p>
              <p className="text-sm text-primary/80">Total Contract Employees</p>
            </button>
          </div>
        </div>
      )}

      {/* Quick access tiles */}
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-4">Quick Access</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {filteredTiles.map((tile) => (
            <button
              key={tile.id}
              onClick={() => navigate(tile.path)}
              data-testid={`tile-${tile.id}`}
              className={cn(
                "stat-tile group cursor-pointer text-left",
                "bg-gradient-to-br", tile.color, "to-transparent"
              )}
            >
              <tile.icon className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
              <p className="font-medium text-foreground">{tile.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Charts section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Department breakdown */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Employees by Department</h3>
          {deptData.length > 0 ? (
            <div className="flex items-center gap-6">
              <div className="w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={deptData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {deptData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1E293B', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                {deptData.map((dept) => (
                  <div key={dept.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />
                    <span className="text-sm text-muted-foreground flex-1">{dept.name}</span>
                    <span className="text-sm font-medium">{dept.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground">
              No department data available
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Pending Actions</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="font-medium">Pending Leave Requests</p>
                  <p className="text-sm text-muted-foreground">Awaiting approval</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-amber-400">{stats?.pending_leave_requests || 0}</span>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Unread Notifications</p>
                  <p className="text-sm text-muted-foreground">Requires attention</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-primary">{stats?.unread_notifications || 0}</span>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Scale className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="font-medium">Open Disciplinary Cases</p>
                  <p className="text-sm text-muted-foreground">Active investigations</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-red-400">{stats?.open_disciplinary_cases || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, loading, color, bgColor, testId }) {
  return (
    <div className="stat-tile" data-testid={testId}>
      <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center mb-4", bgColor)}>
        <Icon className={cn("w-6 h-6", color)} />
      </div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-3xl font-bold text-foreground mt-1">
        {loading ? '...' : value}
      </p>
    </div>
  );
}
